<script setup lang="ts">
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import ProgressFrame from './layout/ProgressFrame.vue';
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { optimisticallyGetCommissionState } from '../utils/output-connections';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { projectHeaderDisplay } from '../utils/project-and-site';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import { message } from 'ant-design-vue';
import WarningBox from '../components/WarningBox.vue';

const router = useRouter();
const deviceSpecificationStore = useDeviceSpecificationStore();
const commissioningDeviceStore = useCommissioningDeviceStore();

onMounted(() => {
  optimisticallyGetCommissionState();
});

const serialNumber = commissioningDeviceStore.serialNumber;

const siteToDisplay = computed(() => deviceSpecificationStore.siteToConfigure);
const projectHeader = computed(() => {
  if (!siteToDisplay.value) return null;
  return projectHeaderDisplay(
    siteToDisplay.value.projectId,
    siteToDisplay.value.projectName ?? null,
  );
});

const confirmSiteToConfigure = async () => {
  try {
    await deviceSpecificationStore.saveMeterSiteId(serialNumber);
    router.push({ name: 'ConfigureSolshareSystemSpec' });
  } catch (error) {
    console.error('Failed to confirm site: ', error);
    message.error('Failed to confirm site. Please try again.');
  }
};
</script>

<template>
  <ProgressFrame pageClass="section-page" titleTight>
    <template #title>Configure the SolShare</template>
    <template #subTitle>
      <div class="serial-number">{{ serialNumber }}</div>
    </template>

    <div class="content">
      <div class="confirm-site__container">
        <p>We're going to start configuring the SolShare at:</p>

        <div class="project-name" data-tn="project-name-container">
          <span class="project-name__title">{{ projectHeader?.heading }}</span>
          <span v-if="projectHeader?.subheading" class="project-name__subtitle">
            {{ projectHeader?.subheading }}
          </span>
        </div>

        <template v-if="siteToDisplay?.siteAddress">
          <div class="site-address__container" data-tn="site-address-container">
            <div class="site-address__text h4">
              {{ siteToDisplay.siteAddress }}
            </div>
          </div>
        </template>
        <template v-else>
          <WarningBox type="error">
            <template #title>No site address found</template>
            <template #message>Please select a different site.</template>
          </WarningBox>
        </template>
      </div>

      <div class="end-of-page">
        <ButtonPrimary
          block
          class="next-button"
          @click="confirmSiteToConfigure"
          data-tn="goto-next-btn"
        >
          Let's go!
        </ButtonPrimary>

        <div class="select-different-site">
          Not the right site address?
          <span class="link__container">
            <RouterLink
              :to="{ name: 'ProjectSelect' }"
              @click="deviceSpecificationStore.clearSelectedSite()"
              class="link"
              data-tn="project-list-link"
            >
              <span>Select a different site</span>
            </RouterLink>
            here.
          </span>
        </div>
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.serial-number {
  font-weight: @font-weight-medium;
}

.confirm-site__container {
  width: 100%;
  padding-top: @content-side-margin;

  text-align: center;
  color: @color-primary;
}

.project-name {
  &__title {
    font-weight: @font-weight-medium;
  }

  &__subtitle {
    display: block;
  }
}

.site-address {
  &__container {
    width: 100%;
    background-color: white;
    border: 1px solid @color-primary;
    border-radius: @border-radius-medium;

    margin-top: 10px;
    margin-bottom: 10px;
  }

  &__text {
    padding: @content-side-margin;
    font-weight: @font-weight-medium;
  }
}

.end-of-page {
  margin-top: @margin-top-end-of-page-section;
  width: 100%;

  .next-button {
    margin-bottom: @margin-bottom-button;
  }

  .select-different-site {
    color: @color-primary;
    text-align: center;

    .link__container {
      display: block;

      .link {
        color: @color-primary;
        text-decoration: underline;
      }
    }
  }
}
</style>
